// Cache for confidence scores to avoid duplicate API calls
import React, { useEffect, useMemo, useState } from 'react';

import { Chip, Tooltip } from '@mui/material';

import HotelRoomMatchingService from '~/services/HotelRoomMatchingService';

const confidenceCache = new Map<string, { score: number; threshold: number }>();

// Component to display confidence score for a mapping
export default function MappingConfidence({
  sourceRoomName,
  propertyRoomName,
}: {
  sourceRoomName?: string;
  propertyRoomName: string;
}) {
  const [confidence, setConfidence] = useState<{ score: number; threshold: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create a stable cache key
  const cacheKey = useMemo(() => {
    if (!sourceRoomName) {
      return null;
    }
    return `${sourceRoomName}-${propertyRoomName}`;
  }, [sourceRoomName, propertyRoomName]);

  useEffect(() => {
    // Skip API call if source room name is missing
    if (!cacheKey || !sourceRoomName) {
      return;
    }

    const fetchConfidence = async () => {
      // Check cache first to avoid duplicate API calls
      if (confidenceCache.has(cacheKey)) {
        setConfidence(confidenceCache.get(cacheKey));
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await HotelRoomMatchingService.rankRoomMatchCandidates(sourceRoomName, [propertyRoomName]);

        // Handle case where ranking might be empty
        const matchScore = response.ranking.length > 0 ? response.ranking[0].score : 0;
        const result = {
          score: matchScore,
          threshold: response.confidenceThreshold,
        };

        // Store in cache to avoid future API calls for the same pair
        confidenceCache.set(cacheKey, result);
        setConfidence(result);
      } catch (error) {
        console.error('Failed to get room matching confidence:', error);
        setError('Failed to load confidence score');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfidence();
  }, [cacheKey, sourceRoomName, propertyRoomName]);

  if (!sourceRoomName) {
    return null;
  }
  if (isLoading) {
    return (
      <Tooltip title="Testing match confidence...">
        <Chip label="Testing match..." color="info" size="small" />
      </Tooltip>
    );
  }
  if (error) {
    return null;
  }
  if (!confidence) {
    return null;
  }

  const confidencePercent = (confidence.score * 100).toFixed(1);
  const isHighConfidence = confidence.score > confidence.threshold;

  return (
    <Tooltip title={`Match confidence: ${confidencePercent}%`}>
      <Chip label={`${confidencePercent}% match`} color={isHighConfidence ? 'success' : 'warning'} size="small" />
    </Tooltip>
  );
}
